import React, { Fragment, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLoader } from "../../_helper/LoaderContext";
import { usePharmacy } from "../../_helper/PharmacyProvider";
import { Breadcrumbs } from "../../AbstractElements";
import { Card, CardBody, Col, Row } from "reactstrap";
import { Percent, Clock, Package, ShoppingBag, Info, Check, DollarSign } from "react-feather";
import InfoIcon from "../PharmacyStocks/components/InfoIcon";
import { updatePharmacyProfile } from "../Profile/Service/ManageClinicUsersService";
import { toast } from "react-toastify";
import {
    defaultTaxConfig,
    getTaxAppliedToValue,
    getTaxConfigFromPharmacyPOS,
} from "./taxConfigUtils";
import "./ManageSettings.scss";

const ManageSetting = () => {
    const { t } = useTranslation();
    const { setLoading } = useLoader();
    const { pharmacyPOS, setPharmacyPOS, refreshConversionRates } = usePharmacy();

    const [conversionRates, setConversionRates] = useState({});
    const [expiryAlertDays, setExpiryAlertDays] = useState("");
    const [taxConfig, setTaxConfig] = useState(defaultTaxConfig);

    const isGst = taxConfig.taxType === "gst";
    const isVat = taxConfig.taxType === "vat";
    const isTaxEnabled = taxConfig.taxEnabled;
    const isTaxTypeSelected = isTaxEnabled && Boolean(taxConfig.taxType);

    const baseCurrency = pharmacyPOS?.baseCurrency || "INR";
    const allowedCurrencies = pharmacyPOS?.allowedCurrencies || [];

    /* ---------- INIT ---------- */
    useEffect(() => {
        if (!pharmacyPOS) return;

        setExpiryAlertDays(pharmacyPOS.expiry_alert_duration || "");
        setTaxConfig(getTaxConfigFromPharmacyPOS(pharmacyPOS));

        let isMounted = true;

        const loadRates = async () => {
            const result = await refreshConversionRates();

            if (!isMounted) return;

            setConversionRates(
                result.rates || pharmacyPOS.conversionRates || {}
            );
        };

        loadRates();

        return () => {
            isMounted = false;
        };
    }, [pharmacyPOS?._id, refreshConversionRates]);

    /* ---------- HANDLERS ---------- */
    const handleRateChange = (currency, value) => {
        setConversionRates(prev => ({
            ...prev,
            [currency]: parseFloat(value) || 0
        }));
    };

    const saveConversionRates = async () => {
        setLoading(true);
        try {
            const payload = { conversionRates };
            const res = await updatePharmacyProfile(payload);

            if (res.data.status === "success") {
                toast.success(res.data.message || t("Currency conversion rates updated"));
                localStorage.setItem("currentPharmacyPOS", JSON.stringify(res.data.data));
                setPharmacyPOS(res.data.data);
            }
        } catch (err) {
            toast.warning(err || err.message || t("Failed to update conversion rates"));
        } finally {
            setLoading(false);
        }
    };

    const handleTaxEnabledChange = () => {
        setTaxConfig((prev) => ({
            ...prev,
            taxEnabled: !prev.taxEnabled,
        }));
    };

    const handleTaxTypeChange = (taxType) => {
        if (!taxConfig.taxEnabled) return;
        setTaxConfig((prev) => ({
            ...prev,
            taxType,
        }));
    };

    const handleApplyToChange = (field) => {
        if (!taxConfig.taxEnabled || !taxConfig.taxType) return;
        setTaxConfig((prev) => {
            const nextValue = !prev[field];
            const otherField = field === "applyToMedicine" ? "applyToOtc" : "applyToMedicine";

            if (!nextValue && !prev[otherField]) {
                toast.info(t("Please select at least one product type to apply tax"));
                return prev;
            }

            return { ...prev, [field]: nextValue };
        });
    };

    const stopApplyItemInfoToggle = (event) => {
        event.preventDefault();
        event.stopPropagation();
    };

    const saveTaxConfiguration = async () => {
        if (!taxConfig.taxEnabled) {
            setLoading(true);
            try {
                const res = await updatePharmacyProfile({ taxType: "none" });
                if (res.data.status === "success") {
                    toast.success(t(res.data.message));
                    localStorage.setItem("currentPharmacyPOS", JSON.stringify(res.data.data));
                    setPharmacyPOS(res.data.data);
                }
            } catch (err) {
                console.log(err.response?.data?.message);
                // toast.warning(t(err.response?.data?.message || "Failed to save tax configuration"));
            } finally {
                setLoading(false);
            }
            return;
        }

        if (!taxConfig.taxType) {
            toast.info(t("Please select a tax type"));
            return;
        }

        const taxAppliedTo = getTaxAppliedToValue(
            taxConfig.applyToMedicine,
            taxConfig.applyToOtc
        );

        if (!taxAppliedTo) {
            toast.info(t("Please select at least one product type to apply tax"));
            return;
        }

        setLoading(true);
        try {
            const payload = {
                taxType: taxConfig.taxType,
                taxAppliedTo,
            };
            const res = await updatePharmacyProfile(payload);
            if (res.data.status === "success") {
                toast.success(t(res.data.message));
                localStorage.setItem("currentPharmacyPOS", JSON.stringify(res.data.data));
                setPharmacyPOS(res.data.data);
            }
        } catch (err) {
            toast.warning(t(err.response?.data?.message || "Failed to save tax configuration"));
        } finally {
            setLoading(false);
        }
    };

    const saveExpiryAlert = async () => {
        if (!expiryAlertDays || expiryAlertDays <= 0) {
            toast.info(t("Please enter valid expiry alert duration"));
            return;
        }

        setLoading(true);
        try {
            const payload = { expiry_alert_duration: Number(expiryAlertDays) };
            const res = await updatePharmacyProfile(payload);

            if (res.data.status === "success") {
                toast.success(t("Expiry alert duration updated"));
                localStorage.setItem("currentPharmacyPOS", JSON.stringify(res.data.data));
                setPharmacyPOS(res.data.data);
            }
        } catch (err) {
            toast.warning(t("Failed to update expiry alert"));
        } finally {
            setLoading(false);
        }
    };

    return (
        <Fragment>
            <Breadcrumbs
                mainTitle={t("Manage Settings")}
                parent={t("Dashboard")}
                title={t("Settings")}
            />

            <Card>
                <CardBody className="manage-settings-page">
                    <Row className="g-3 manage-settings-grid">
                        {/* Tax Configuration */}
                        <Col lg="4">
                            <section className="settings-panel settings-panel--tax">
                                <header className="settings-panel__header">
                                    <span className="settings-panel__icon">
                                        <Percent size={16} />
                                    </span>
                                    <div className="settings-panel__title-wrap">
                                        <h5 className="settings-panel__title">{t("Tax Configuration")}</h5>
                                        <p className="settings-panel__subtitle">
                                            {isTaxEnabled
                                                ? t("Select tax type, then choose product types.")
                                                : t("Tax is currently disabled for this pharmacy.")}
                                        </p>
                                    </div>
                                    <label className="settings-master-toggle" htmlFor="taxEnabled">
                                        <input
                                            id="taxEnabled"
                                            type="checkbox"
                                            checked={isTaxEnabled}
                                            onChange={handleTaxEnabledChange}
                                        />
                                        <span className="settings-master-toggle__track" aria-hidden="true">
                                            <span className="settings-master-toggle__thumb" />
                                        </span>
                                        <span className="settings-master-toggle__label">
                                            {isTaxEnabled ? t("Enabled") : t("Disabled")}
                                        </span>
                                    </label>
                                </header>

                                <div className={`settings-panel__body ${!isTaxEnabled ? "settings-panel__body--disabled" : ""}`}>
                                    <div className={`settings-segment ${!isTaxEnabled ? "settings-segment--disabled" : ""}`}>
                                        <span className="settings-segment__label">{t("Tax Type")}</span>
                                        <div className="settings-choice-row">
                                            <button
                                                type="button"
                                                className={`settings-choice-btn ${isGst ? "active" : ""}`}
                                                disabled={!isTaxEnabled}
                                                onClick={() => handleTaxTypeChange("gst")}
                                            >
                                                <div className="settings-choice-btn__top">
                                                    <p className="settings-choice-btn__name">{t("GST")}</p>
                                                    <span className="settings-choice-btn__badge">
                                                        {isGst ? t("On") : t("Off")}
                                                    </span>
                                                </div>
                                                <p className="settings-choice-btn__hint">
                                                    {t("CGST, SGST & IGST")}
                                                </p>
                                            </button>
                                            <button
                                                type="button"
                                                className={`settings-choice-btn ${isVat ? "active" : ""}`}
                                                disabled={!isTaxEnabled}
                                                onClick={() => handleTaxTypeChange("vat")}
                                            >
                                                <div className="settings-choice-btn__top">
                                                    <p className="settings-choice-btn__name">{t("VAT")}</p>
                                                    <span className="settings-choice-btn__badge">
                                                        {isVat ? t("On") : t("Off")}
                                                    </span>
                                                </div>
                                                <p className="settings-choice-btn__hint">
                                                    {t("Single VAT rate")}
                                                </p>
                                            </button>
                                        </div>
                                    </div>

                                    <div className={`settings-segment ${!isTaxTypeSelected ? "settings-segment--disabled" : ""}`}>
                                        <span className="settings-segment__label">{t("Apply Tax To")}</span>
                                        <div className="settings-apply-grid">
                                            <label
                                                className={`settings-apply-item ${taxConfig.applyToMedicine ? "active" : ""} ${!isTaxTypeSelected ? "disabled" : ""}`}
                                                htmlFor="applyToMedicine"
                                            >
                                                <input
                                                    type="checkbox"
                                                    id="applyToMedicine"
                                                    checked={taxConfig.applyToMedicine}
                                                    disabled={!isTaxTypeSelected}
                                                    onChange={() => handleApplyToChange("applyToMedicine")}
                                                />
                                                <span className="settings-apply-item__icon">
                                                    <Package size={15} />
                                                </span>
                                                <span className="settings-apply-item__text">
                                                    <span className="settings-apply-item__title-row">
                                                        <span className="settings-apply-item__title">{t("Medicine")}</span>
                                                        <span
                                                            className="settings-apply-item__info"
                                                            onClick={stopApplyItemInfoToggle}
                                                            onMouseDown={stopApplyItemInfoToggle}
                                                        >
                                                            <InfoIcon
                                                                text={t(
                                                                    "Includes allopathic medicines, Unani (U Medicine), and any other medicine product types added in the future."
                                                                )}
                                                            />
                                                        </span>
                                                    </span>
                                                </span>
                                                <span className="settings-apply-item__check" aria-hidden="true">
                                                    {taxConfig.applyToMedicine && <Check size={12} />}
                                                </span>
                                            </label>
                                            <label
                                                className={`settings-apply-item ${taxConfig.applyToOtc ? "active" : ""} ${!isTaxTypeSelected ? "disabled" : ""}`}
                                                htmlFor="applyToOtc"
                                            >
                                                <input
                                                    type="checkbox"
                                                    id="applyToOtc"
                                                    checked={taxConfig.applyToOtc}
                                                    disabled={!isTaxTypeSelected}
                                                    onChange={() => handleApplyToChange("applyToOtc")}
                                                />
                                                <span className="settings-apply-item__icon">
                                                    <ShoppingBag size={15} />
                                                </span>
                                                <span className="settings-apply-item__text">
                                                    <span className="settings-apply-item__title">{t("OTC")}</span>
                                                </span>
                                                <span className="settings-apply-item__check" aria-hidden="true">
                                                    {taxConfig.applyToOtc && <Check size={12} />}
                                                </span>
                                            </label>
                                        </div>
                                    </div>

                                    <p className="settings-inline-note">
                                        <Info size={13} />
                                        <span>
                                            {isTaxEnabled
                                                ? t("Tax rates are set per product. This controls the tax system and product scope.")
                                                : t("Saving will set tax type to none and tax will not apply during billing.")}
                                        </span>
                                    </p>
                                </div>

                                <footer className="settings-panel__footer">
                                    <button
                                        type="button"
                                        className="btn btn-primary"
                                        onClick={saveTaxConfiguration}
                                    >
                                        {t("Save Tax")}
                                    </button>
                                </footer>
                            </section>
                        </Col>

                        {/* Currency Conversion */}
                        <Col lg="4">
                            <section className="settings-panel settings-panel--currency">
                                <header className="settings-panel__header">
                                    <span className="settings-panel__icon">
                                        <DollarSign size={16} />
                                    </span>
                                    <div className="settings-panel__title-wrap">
                                        <h5 className="settings-panel__title">{t("Currency Conversion")}</h5>
                                        <p className="settings-panel__subtitle">
                                            {t("Set exchange rates from your base currency.")}
                                        </p>
                                    </div>
                                </header>

                                <div className="settings-panel__body">
                                    <p className="settings-base-currency">
                                        {t("Base Currency")}: <strong>{baseCurrency}</strong>
                                    </p>

                                    <div className="settings-rate-list">
                                        {allowedCurrencies.map((currency) => {
                                            if (currency === baseCurrency) return null;

                                            return (
                                                <div className="settings-rate-row" key={currency}>
                                                    <label className="form-label fw-semibold mb-0">
                                                        1 {baseCurrency} =
                                                    </label>
                                                    <div className="input-group input-group-sm">
                                                        <input
                                                            type="number"
                                                            placeholder="0"
                                                            value={conversionRates[currency] ?? ""}
                                                            onChange={(e) =>
                                                                handleRateChange(currency, e.target.value)
                                                            }
                                                            className="form-control"
                                                        />
                                                        <span className="input-group-text">{currency}</span>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>

                                <footer className="settings-panel__footer">
                                    <button
                                        className="btn btn-primary"
                                        onClick={saveConversionRates}
                                    >
                                        {t("Save Rates")}
                                    </button>
                                </footer>
                            </section>
                        </Col>

                        {/* Expiry Alert */}
                        <Col lg="4">
                            <section className="settings-panel settings-panel--expiry">
                                <header className="settings-panel__header">
                                    <span className="settings-panel__icon">
                                        <Clock size={16} />
                                    </span>
                                    <div className="settings-panel__title-wrap">
                                        <h5 className="settings-panel__title">{t("Expiry Alert")}</h5>
                                        <p className="settings-panel__subtitle">
                                            {t("Notify before products reach expiry.")}
                                        </p>
                                    </div>
                                </header>

                                <div className="settings-panel__body">
                                    <div className="settings-expiry-field">
                                        <label className="form-label" htmlFor="expiryAlertDays">
                                            {t("Alert Before (Days)")}
                                        </label>
                                        <input
                                            id="expiryAlertDays"
                                            type="number"
                                            min="1"
                                            className="form-control"
                                            value={expiryAlertDays}
                                            onChange={(e) => setExpiryAlertDays(e.target.value)}
                                            placeholder={t("e.g. 30")}
                                        />
                                    </div>

                                    <p className="settings-inline-note">
                                        <Info size={13} />
                                        <span>
                                            {t("Items nearing expiry within this window will appear in alerts.")}
                                        </span>
                                    </p>
                                </div>

                                <footer className="settings-panel__footer">
                                    <button
                                        className="btn btn-primary"
                                        onClick={saveExpiryAlert}
                                    >
                                        {t("Save Alert")}
                                    </button>
                                </footer>
                            </section>
                        </Col>
                    </Row>
                </CardBody>
            </Card>
        </Fragment>
    );
};

export default ManageSetting;
